import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PasConfirmationService, PasSystemProcessComponent } from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import * as fileSaver from 'file-saver';

import { AttachmentService } from '../../../services/attachment/attachment.service';
import {
  showAttachmentDeleteFailed,
  showAttachmentDeleteSuccess,
  showAttachmentDownloadFailed,
} from '../../../services/booking/recourse-booking-functions';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { UserService } from '../../../services/user-service/user.service';
import { setGridHeightConditionally } from '../../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { AttachmentModel } from '../model/attachment.model';
import { DeleteDownloadCellRenderer } from '../renderer/delete-download-cell-renderer/delete-download-cell-renderer.component';

@Component({
  selector: 'app-booking-attachments',
  templateUrl: './booking-attachments.component.html',
  imports: [AgGridAngular, PasSystemProcessComponent],
})
export class BookingAttachmentsComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly agGridService = inject(AgGridService);
  private readonly userService = inject(UserService);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly attachmentService = inject(AttachmentService);

  @Input()
  public bookingAttachments: AttachmentModel[];
  @Input()
  public isNewTabActive: boolean;
  @ViewChild('processComponentAttachment')
  public processComponentAttachment: PasSystemProcessComponent;
  @Output() public deleteAttachment = new EventEmitter();

  public gridOptions: GridOptions;
  private gridApi: GridApi;
  public columnDefs: any;
  public defaultColDef: any;
  public frameworkComponents: any;

  private tmpParam: any;

  public ngOnInit(): void {
    this.setupGrid();
  }

  private setupGrid(): void {
    this.gridOptions = this.agGridService.newDefaultGridOptions();
    this.gridOptions.getLocaleText = this.agGridService.translate(this.userService, this.translateService);
    this.gridOptions.overlayComponentParams = {
      noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
    };
    this.gridOptions.pagination = false;
    this.gridOptions.suppressCellFocus = true;
    this.frameworkComponents = {
      deleteDownloadCellRenderer: DeleteDownloadCellRenderer,
    };
    this.defaultColDef = {
      editable: false,
      resizable: true,
      sortable: true,
      floatingFilter: false,
      minWidth: 100,
      unSortIcon: true,
      filter: true,
    };
    this.columnDefs = [
      {
        headerName: this.translateService.instant('BOOKING_DETAILS_ATTACHMENTS_FILE_NAME'),
        field: 'filename',
        width: 120,
      },
      {
        headerName: this.translateService.instant('BOOKING_DETAILS_ATTACHMENTS_CREATOR'),
        field: 'creator',
        width: 120,
      },
      {
        headerName: this.translateService.instant('BOOKING_DETAILS_ATTACHMENTS_DATE'),
        field: 'createDate',
        width: 120,
      },
      { headerName: this.translateService.instant('BOOKING_DETAILS_ATTACHMENTS_RECEIVER'), field: 'receiver' },
      {
        headerName: '',
        field: 'value',
        cellRenderer: 'deleteDownloadCellRenderer',
        cellRendererParams: {
          onDownload: this.onDownload.bind(this),
          onDelete: this.onDelete.bind(this),
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 80,
      },
    ];
  }

  public onGridSizeChanged(event: GridSizeChangedEvent<any>): void {
    event.api.sizeColumnsToFit();
  }

  public onGridReady(event: GridReadyEvent): void {
    this.tmpParam = event;
    this.gridApi = event.api;
    this.gridOptions.overlayNoRowsTemplate = `<span>${this.translateService.instant(
      'BOOKING_DETAILS_NO_ATTACHMENTS_MESSAGE',
    )}</span>`;
    this.gridOptions.onRowDataUpdated = () => this.setAttachmentsGridHeight();
    this.gridApi.sizeColumnsToFit();
    event.api.setGridOption('rowData', this.bookingAttachments);
    this.setAttachmentsGridHeight();
  }

  private onDownload(event): void {
    const fileName = event.rowData.filename;
    this.attachmentService.download(event.rowData.id).subscribe({
      next: response => {
        const blob = new Blob([response], { type: response.type });
        fileSaver.saveAs(blob, event.rowData.filename);
      },
      error: () => {
        showAttachmentDownloadFailed(this.notificationServiceWrapper, this.translateService, fileName);
      },
      complete: () => {},
    });
  }

  private onDelete(event): void {
    this.translateService
      .get(['DELETE_ATTACHMENT_TITLE', 'DELETE_ATTACHMENT_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.DELETE_ATTACHMENT_TITLE;
        const message: string = translate.DELETE_ATTACHMENT_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, true).then(
          () => {
            if (!this.isNewTabActive) {
              this.processComponentAttachment.startProcess();
              const fileName = event.rowData.filename;
              this.attachmentService.delete(event.rowData.id).subscribe({
                next: () => {
                  this.deleteAttachment.emit();
                  this.processComponentAttachment.stopProcess();
                  showAttachmentDeleteSuccess(this.notificationServiceWrapper, this.translateService, fileName);
                },
                error: () => {
                  this.processComponentAttachment.stopProcess();
                  showAttachmentDeleteFailed(this.notificationServiceWrapper, this.translateService, fileName);
                },
              });
            }
            const deleteItems = [event.rowData];
            this.gridApi.applyTransaction({ remove: deleteItems });
          },
          () => {},
        );
      });
  }

  public refreshTable(attachments: AttachmentModel[]): void {
    this.bookingAttachments = attachments;
    this.onGridReady(this.tmpParam);
  }

  public applyTransactionAdd(attachmentIds: AttachmentModel[]): void {
    this.gridApi.applyTransaction({ add: attachmentIds });
  }

  private setAttachmentsGridHeight(): void {
    setGridHeightConditionally(this.gridApi, '#attachmentsTable');
  }
}
